import App, { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import { Router } from 'next/router';
import React, { useEffect } from 'react';

import cssVars from 'css-vars-ponyfill';
import { getAnalytics } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import $ from 'jquery';
import moment from 'moment';
import 'moment-timezone';
import NProgress from 'nprogress';
import Cookies from 'universal-cookie';
import verifyWhetherDisplayBlackBackgroundCampaign from 'utils/verifyWhetherDisplayBlackBackgroundCampaign';

import AppContextProvider from 'contexts/appContext';
import AuthContextProvider from 'contexts/authContext';
import CartContextProvider from 'contexts/cartContext';
import RecaptchaContextProvider from 'contexts/RecaptchaContext';
import { IDonationCampaign } from 'models/donationCampaign';
import { IShoppingCart } from 'models/shoppingCart';
import { IUser } from 'models/user';
import { DonationCampaignClientService } from 'services/donationCampaignClientService';
import { fetcher } from 'services/httpRequestor';
import { SettingClientService } from 'services/SettingClientService';
import { ShoppingCartClient } from 'services/shoppingCartClient';
import { UserService } from 'services/userService';

import * as gtag from '../utils/gtag';

import '../styles/index.scss';
import 'react-sweet-progress/lib/style.css';

// Initialize Firebase (only in browser)
if (typeof window !== 'undefined') {
  const firebaseConfig = ENV.FIREBASE_WEB_SDK_CONFIG;

  if (firebaseConfig) {
    try {
      const app = initializeApp(firebaseConfig);
      getAnalytics(app);
      getAuth(app);
    } catch (error) {
      console.error('Firebase initialization failed:', error);
    }
  }
}

interface IProps extends AppProps {
  host: string;
  initialProps: {
    user: IUser | undefined;
    userCart: IShoppingCart | undefined;
    isServer: boolean;
    donationCampaigns: IDonationCampaign[];
    menuStructure: any;
    menu404Structure: any;
    footerMenuStructure: any;
    displayBlackBackgroundCampaign: boolean;
    blackBackgroundCampaignConfiguration: any;
    xmasBanner: any;
    featuredDonationCampaignSlug?: string;
    featuredGiftGivingCampaignSlug?: string;
    thankYouPageFeaturedCampaignSlug?: string;
    productListCampaignSuggestedItems?: string;
    facebookFoundationPage?: string;
    instagramFoundationUser?: string;
    twitterFoundationUser?: string;
    youtubeFoundationChannel?: string;
    festiveTreeSlug?: string;
    festiveInstagramPost?: string;
    paypalThreshold?: number;
    applepayEnabled?: string;
    googlepayEnabled?: string;
    paypalEnabled?: string;
    animationsS3Url?: string;
  };
}

// Router events for progress bar
Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeError', () => NProgress.done());
Router.events.on('routeChangeComplete', (url: string) => {
  gtag.pageview(url);
  NProgress.done();
});

function MyApp({ Component, pageProps, initialProps, host }: IProps) {
  useEffect(() => {
    // Make jQuery available globally for HubSpot iframe
    if (typeof window !== 'undefined') {
      (window as any).$ = $;
      (window as any).jQuery = $;
    }
  }, []);

  // Initialize CSS variables polyfill
  useEffect(() => {
    cssVars({
      // Options...
    });
  }, []);

  // Get environment variables
  const googlePlacesAPIKey = ENV?.GOOGLE_PLACES_API_KEY;
  const westpacUrl = ENV?.WESTPAC_URL;

  return (
    <AppContextProvider serverInitialState={initialProps}>
      <AuthContextProvider serverInitialState={initialProps}>
        <RecaptchaContextProvider>
          <CartContextProvider serverInitialState={initialProps}>
            <Head>
              <link rel="manifest" href="/manifest.json" />
              <meta charSet="utf-8" />
              <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
              <meta
                name="viewport"
                content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=5"
              />
              <meta name="theme-color" content="red" />
              <meta name="mobile-web-app-capable" content="yes" />
              <meta
                name="apple-mobile-web-app-title"
                content="Surf Life Saving Giving"
              />
              <meta name="apple-mobile-web-app-capable" content="yes" />
              <meta
                name="apple-mobile-web-app-status-bar-style"
                content="default"
              />
              <meta name="msapplication-navbutton-color" content="red" />
              <meta name="msapplication-TileColor" content="red" />
              <meta
                name="msapplication-TileImage"
                content="ms-icon-144x144.png"
              />
              <meta name="msapplication-config" content="browserconfig.xml" />
              <meta name="application-name" content="SLSF" />
              <meta
                name="msapplication-tooltip"
                content="Surf Life Saving Giving"
              />
              <meta name="msapplication-starturl" content="/" />
              <meta name="msapplication-tap-highlight" content="no" />
              <meta name="full-screen" content="yes" />
              <meta name="browsermode" content="application" />
              <meta name="nightmode" content="enable/disable" />
              <meta name="layoutmode" content="fitscreen/standard" />
              <meta name="imagemode" content="force" />
              <meta name="screen-orientation" content="portrait" />
              <title>Surf Life Saving Giving</title>
            </Head>
            <Component host={host} {...pageProps} />
            {googlePlacesAPIKey && (
              <script
                src={`https://maps.googleapis.com/maps/api/js?key=${googlePlacesAPIKey}&libraries=places`}
              />
            )}
            {westpacUrl && (
              <script src={`${westpacUrl}/quickstream-api-1.0.min.js`} />
            )}
          </CartContextProvider>
        </RecaptchaContextProvider>
      </AuthContextProvider>
    </AppContextProvider>
  );
}

const cookieContainsApiToken = (appContext: AppContext) => {
  const cookies = new Cookies(appContext.ctx.req?.headers.cookie);
  return !!cookies.get('api_token');
};

const getAuthorisedUser = async (appContext: AppContext) => {
  if (typeof window !== 'undefined' || !cookieContainsApiToken(appContext)) {
    return undefined;
  }

  try {
    const cookies = appContext.ctx.req?.headers.cookie;
    const userService = new UserService(fetcher(cookies));

    const { data, status } = await userService.getAuthorisedUser();

    return status === 200 ? data : undefined;
  } catch (ex) {
    console.warn(
      'Token might be expired, letting Firebase client handle it in the callback handler. Check next exception',
    );
    console.error(ex);
    return undefined;
  }
};

const getUserCart = async (appContext: AppContext) => {
  if (typeof window !== 'undefined' || !cookieContainsApiToken(appContext)) {
    return undefined;
  }

  const cookies = appContext.ctx.req?.headers.cookie;

  const shoppingCartServerService = new ShoppingCartClient(fetcher(cookies));
  const { status, data } = await shoppingCartServerService.getMyCart();

  return status === 200 ? data : undefined;
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);

  // Determine the base URL for API calls
  const isServer = typeof window === 'undefined';
  const cookies = appContext.ctx.req?.headers.cookie;

  // Create fetcher with proper base URL
  const apiFetcher = isServer ? fetcher(cookies) : fetcher();

  const donationCampaignService = new DonationCampaignClientService(apiFetcher);
  const settingService = new SettingClientService(apiFetcher);

  const [donationCampaigns, settings, user, userCart] = await Promise.all([
    donationCampaignService.getAllDonationsCampaigns().catch(err => {
      console.error('Failed to fetch donation campaigns:', err);
      return [];
    }),
    settingService.getAllSettings().catch(err => {
      console.error('Failed to fetch settings:', err);
      return {};
    }),
    getAuthorisedUser(appContext).catch(err => {
      console.error('Failed to fetch user:', err);
      return undefined;
    }),
    getUserCart(appContext).catch(err => {
      console.error('Failed to fetch cart:', err);
      return undefined;
    }),
  ]);

  const paypalThresholdValue = settings?.paypalThreshold?.value;
  const paypalThreshold =
    paypalThresholdValue != null ? Number(paypalThresholdValue) : undefined;
  const applepayEnabled = settings?.applepayEnabled?.value;
  const googlepayEnabled = settings?.googlepayEnabled?.value;
  const paypalEnabled = settings?.paypalEnabled?.value;
  const menu404Structure = settings?.menu404Structure?.jsonValue || [];
  const menuStructure = settings?.menuStructure?.jsonValue || [];
  const footerMenuStructure = settings?.footerMenuStructure?.jsonValue || [];
  const xmasBanner = settings?.xmasBanner?.jsonValue || {};
  const blackBackgroundCampaignConfiguration =
    settings?.blackBackgroundCampaignConfiguration?.jsonValue || {};
  const featuredDonationCampaignSlug =
    settings?.featuredDonationCampaignSlug?.value;
  const featuredGiftGivingCampaignSlug =
    settings?.featuredGiftGivingCampaignSlug?.value;
  const productListCampaignSuggestedItems =
    settings?.productListCampaignSuggestedItems?.value;
  const facebookFoundationPage = settings?.facebookFoundationPage?.value;
  const instagramFoundationUser = settings?.instagramFoundationUser?.value;
  const twitterFoundationUser = settings?.twitterFoundationUser?.value;
  const youtubeFoundationChannel = settings?.youtubeFoundationChannel?.value;
  const festiveTreeSlug = settings?.festiveTreeSlug?.value;
  const festiveInstagramPost = settings?.festiveInstagramPost?.value;
  const animationsS3Url = settings?.animationsS3Url?.value;
  const thankYouPageFeaturedCampaignSlug =
    settings?.thankYouPageFeaturedCampaignSlug?.value;

  let host;
  if (typeof window !== 'undefined') {
    host = window.location.origin;
  } else {
    host = `https://${appContext?.ctx?.req?.headers.host}`;
  }

  return {
    ...appProps,
    host,
    initialProps: {
      user,
      userCart,
      donationCampaigns,
      paypalThreshold,
      applepayEnabled,
      googlepayEnabled,
      paypalEnabled,
      menuStructure,
      menu404Structure,
      footerMenuStructure,
      featuredDonationCampaignSlug,
      featuredGiftGivingCampaignSlug,
      thankYouPageFeaturedCampaignSlug,
      productListCampaignSuggestedItems,
      facebookFoundationPage,
      instagramFoundationUser,
      twitterFoundationUser,
      youtubeFoundationChannel,
      festiveTreeSlug,
      festiveInstagramPost,
      blackBackgroundCampaignConfiguration,
      displayBlackBackgroundCampaign: blackBackgroundCampaignConfiguration
        ? verifyWhetherDisplayBlackBackgroundCampaign(
            blackBackgroundCampaignConfiguration.startDate,
            blackBackgroundCampaignConfiguration.endDate,
            moment().tz('Australia/Sydney').toISOString(),
          )
        : false,
      xmasBanner,
      isServer: !!appContext.ctx.req,
      animationsS3Url,
    },
  };
};

export default MyApp;
